import Button from "@imminently/decisively-ui/Button";
import Dialog from "@imminently/decisively-ui/Dialog";
import DropdownMenu from "@imminently/decisively-ui/DropdownMenu";
import Icon from "@imminently/decisively-ui/Icon";
import { useErrorReporting } from "@/ErrorReporting";
import type { ModalControlsProps } from "@imminently/decisively-ui/Modal";
import type React from "react";

/** Menu item opt-out flags/overrides for the controls more menu. All default to `true` (shown with default action). Pass `false` to hide, or a function to override the action. */
export interface DialogControlsMenuProps {
  feedback?: boolean | (() => void);
  /** Additional `DropdownMenu.Item` nodes rendered after the built-in items. */
  menuContent?: React.ReactNode;
}

/** Props for DialogControls — extends ModalControlsProps with menu item toggles. */
export type DialogControlsProps = ModalControlsProps & DialogControlsMenuProps;

/**
 * Wraps `Dialog.Controls` with a horizontal-dots icon button that opens a dropdown menu.
 * Children are rendered as direct icon buttons before the more menu (for quick-access actions).
 * The more menu button is hidden entirely when all items are disabled.
 */
export const DialogControls = ({ feedback = true, menuContent, children, ...controlsProps }: DialogControlsProps) => {
  const { open } = useErrorReporting();

  const feedbackAction = feedback === true ? open : typeof feedback === "function" ? feedback : null;
  const hasMenuItems = feedbackAction !== null || Boolean(menuContent);

  return (
    <Dialog.Controls {...controlsProps}>
      {children}
      {hasMenuItems && (
        <DropdownMenu>
          <DropdownMenu.Trigger asChild>
            <Button icon tabIndex={-1} label="More options">
              <Icon name="more-horizontal" />
            </Button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Content align="end">
            {feedbackAction && (
              <DropdownMenu.Item onClick={() => feedbackAction()}>
                <DropdownMenu.Item.Icon><Icon name="message-square-share" /></DropdownMenu.Item.Icon>
                <DropdownMenu.Item.Label>Feedback</DropdownMenu.Item.Label>
              </DropdownMenu.Item>
            )}
            {menuContent}
          </DropdownMenu.Content>
        </DropdownMenu>
      )}
    </Dialog.Controls>
  );
};

/** Props for the DialogHeader composite component. */
export interface DialogHeaderProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  controls?: DialogControlsProps;
  className?: string;
}

/**
 * Composes `Dialog.Header`, `Dialog.Title`, an optional `Dialog.Description`,
 * and `DialogControls` into a single component — replacing the repetitive three-liner
 * pattern across modals.
 */
export const DialogHeader = ({ title, description, controls, className }: DialogHeaderProps) => (
  <Dialog.Header className={className}>
    <Dialog.Title>{title}</Dialog.Title>
    {description && <Dialog.Description className="text-muted-foreground">{description}</Dialog.Description>}
    <DialogControls {...controls} />
  </Dialog.Header>
);
